import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Tooltip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  createSourceChannel,
  fetchSourceChannelDetail,
  fetchSourceChannels,
  generateSourceChannelSecret,
  resetSourceForm,
  selectSecretError,
  selectSecretStatus,
  selectSourceChannelById,
  selectSourceChannelDetail,
  selectSourceFormError,
  updateSourceChannel,
} from "../store/slices/sourceChannelsSlice";
import { validateSourceChannelForm } from "../utils/validation";

const defaultValues = {
  name: "",
  status: "ACTIVE",
  secret_key: "",
};

const SourceChannelFormPage = ({ mode = "create" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sourceChannelId } = useParams();
  const isEdit = mode === "edit" && sourceChannelId;
  const channel = useSelector((state) =>
    selectSourceChannelById(state, Number(sourceChannelId))
  );
  const detailState = useSelector(selectSourceChannelDetail);
  const formError = useSelector(selectSourceFormError);
  const secretStatus = useSelector(selectSecretStatus);
  const secretError = useSelector(selectSecretError);

  const [values, setValues] = useState(() => ({ ...defaultValues }));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(resetSourceForm());
    return () => dispatch(resetSourceForm());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && sourceChannelId && !channel) {
      dispatch(fetchSourceChannelDetail(Number(sourceChannelId)));
    }
  }, [dispatch, isEdit, sourceChannelId, channel]);

  useEffect(() => {
    if (isEdit) {
      if (channel) {
        setValues({
          name: channel.name || "",
          status: channel.status || "ACTIVE",
          secret_key: channel.secret_key || "",
        });
      }
    } else {
      setValues({ ...defaultValues });
    }
  }, [isEdit, channel]);

  const title = isEdit ? "Update Source Channel" : "Create Source Channel";
  const subtitle = isEdit
    ? "Edit source channel details and status."
    : "Create a new source channel for integrations.";

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerateSecret = async () => {
    setSubmitError(null);
    try {
      const result = await dispatch(generateSourceChannelSecret()).unwrap();
      const nextSecret =
        result?.secret_key ||
        result?.secretKey ||
        result?.data?.secret_key ||
        result?.data?.secretKey ||
        (typeof result === "string" ? result : "");
      if (nextSecret) {
        setValues((prev) => ({ ...prev, secret_key: nextSecret }));
      }
    } catch (error) {
      setSubmitError(error || "Failed to generate secret key.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError(null);
    const validationErrors = validateSourceChannelForm(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      name: values.name.trim(),
      status: values.status,
      secret_key: values.secret_key.trim(),
    };

    try {
      if (isEdit && sourceChannelId) {
        await dispatch(
          updateSourceChannel({
            id: Number(sourceChannelId),
            payload,
          })
        ).unwrap();
      } else {
        await dispatch(createSourceChannel(payload)).unwrap();
      }
      dispatch(fetchSourceChannels());
      navigate("/source-channels");
    } catch (errorResponse) {
      setSubmitError(
        errorResponse || "An unexpected error occurred while saving the source channel."
      );
      setIsSubmitting(false);
    }
  };

  const statusOptions = useMemo(
    () => [
      { value: "ACTIVE", label: "Active" },
      { value: "INACTIVE", label: "Inactive" },
    ],
    []
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Typography variant="h4" fontWeight={700}>
          {title}
        </Typography>
        <Typography color="text.secondary">{subtitle}</Typography>
        <Paper sx={{ p: 3 }}>
          {isEdit && !channel && detailState.loading && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Loading source channel information...
            </Typography>
          )}
          {isEdit && detailState.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailState.error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={values.name}
                onChange={handleInputChange("name")}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={values.status}
                onChange={handleInputChange("status")}
                error={Boolean(errors.status)}
                helperText={errors.status}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Secret Key"
                value={values.secret_key}
                onChange={handleInputChange("secret_key")}
                error={Boolean(errors.secret_key) || Boolean(secretError)}
                helperText={errors.secret_key || secretError}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Regenerate secret key">
                        <span>
                          <IconButton
                            edge="end"
                            onClick={handleGenerateSecret}
                            disabled={secretStatus === "loading"}
                          >
                            {secretStatus === "loading" ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          {(submitError || formError) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError || formError}
            </Alert>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/source-channels")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default SourceChannelFormPage;
